import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  LogOut, Copy, RefreshCw, Users, Wallet, Calendar, BookOpen,
  MessageCircle, Mail, FileText, Download, CheckCircle2, Phone,
  Link as LinkIcon, Share2,
} from "lucide-react";
import { toast } from "sonner";
import {
  vendorMe, vendorUpdateMe, vendorLeads, vendorCommissions, vendorResources,
  getConfig, formatErr,
} from "../lib/api";

const STATUS_LABEL = {
  pendiente: { label: "Pendiente", color: "#F59E0B" },
  en_proceso: { label: "En proceso", color: "#3B82F6" },
  entregado: { label: "Entregado", color: "#10B981" },
};

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [comm, setComm] = useState(null);
  const [resources, setResources] = useState([]);
  const [config, setConfig] = useState({ whatsapp: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [bizum, setBizum] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, ls, c, rs, cfg] = await Promise.all([
        vendorMe(), vendorLeads(), vendorCommissions(), vendorResources(), getConfig(),
      ]);
      setMe(m); setLeads(ls); setComm(c); setResources(rs); setConfig(cfg);
      setBizum(m.phone_bizum || "");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("el_vendor_token");
        navigate("/vendedores/login");
      } else { toast.error(formatErr(err)); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("el_vendor_token")) { navigate("/vendedores/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("el_vendor_token");
    navigate("/vendedores/login");
  };

  const saveBizum = async () => {
    try {
      const upd = await vendorUpdateMe({ phone_bizum: bizum });
      setMe(upd);
      toast.success("Bizum actualizado");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado"); };

  const downloadContract = async () => {
    try {
      const token = localStorage.getItem("el_vendor_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor/contract.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrato-afiliado-${me.vendor_code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contrato descargado");
    } catch (err) {
      toast.error("No se pudo descargar el contrato");
    }
  };

  const wa = (config.whatsapp || "").replace(/\D/g, "");
  const waUrl = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Hola, necesito soporte (vendedor)")}` : "#";

  if (loading || !me) {
    return (
      <div className="wizard-shell"><div className="wizard-frame flex items-center justify-center">
        <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
      </div></div>
    );
  }

  return (
    <div className="wizard-shell">
      <div className="wizard-frame flex flex-col">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <Logo size={28} withWordmark />
          <div className="flex items-center gap-1">
            <button onClick={load}
              className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="btn-vendor-refresh" aria-label="Recargar">
              <RefreshCw className="w-4 h-4 text-slate-700" />
            </button>
            <button onClick={logout}
              className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="btn-vendor-logout" aria-label="Salir">
              <LogOut className="w-4 h-4 text-slate-700" />
            </button>
          </div>
        </header>

        <main className="flex-1 px-5 py-4 fade-in overflow-auto">
          {/* ID */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[var(--el-red)] to-[#7A1A1E] text-white p-4 shadow-[0_10px_30px_rgba(155,34,38,0.20)]"
               data-testid="vendor-id-card">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-90">Tu ID vendedor</div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <div className="font-[Outfit] font-extrabold text-3xl tracking-[0.3em]" data-testid="vendor-code-value">
                {me.vendor_code}
              </div>
              <button onClick={() => copy(me.vendor_code)}
                className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                data-testid="btn-copy-vendor-code" aria-label="Copiar">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-white/85 mt-2">Comparte este código con tus clientes para que los asocien a ti en el onboarding.</p>
          </div>

          {/* Affiliate Link */}
          <section className="mt-4" data-testid="vendor-affiliate-section">
            <SectionTitle icon={<LinkIcon className="w-4 h-4" />} title="Tu link de afiliado" />
            <AffiliateLink vendorCode={me.vendor_code} />
          </section>

          {/* Contract PDF */}
          <section className="mt-4" data-testid="vendor-contract-section">
            <SectionTitle icon={<FileText className="w-4 h-4" />} title="Contrato de afiliado" />
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/vendor/contract.pdf`}
              onClick={(e) => {
                // Use fetch with auth header to download
                e.preventDefault();
                downloadContract();
              }}
              className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-200 p-3.5 hover:bg-slate-50 active:scale-[0.99] transition cursor-pointer"
              data-testid="btn-download-contract"
            >
              <div className="h-10 w-10 rounded-xl bg-[var(--el-red)]/10 text-[var(--el-red)] flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">Descargar mi contrato</div>
                <div className="text-xs text-slate-500">PDF personalizado con tus datos y código {me.vendor_code}</div>
              </div>
              <Download className="w-4 h-4 text-slate-500" />
            </a>
          </section>

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-2 mt-4" data-testid="vendor-kpis">
            <Kpi label="Clientes" value={comm?.total_clients ?? 0} icon={<Users className="w-3.5 h-3.5" />} />
            <Kpi label="Generada" value={`${comm?.generated ?? 0}€`} icon={<Wallet className="w-3.5 h-3.5" />} accent />
            <Kpi label="Pendiente" value={`${comm?.pending ?? 0}€`} icon={<Calendar className="w-3.5 h-3.5" />} />
          </div>

          <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3.5 flex items-start gap-2.5">
            <Calendar className="w-4 h-4 text-slate-700 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed">
              Próximo pago: <strong>{comm ? new Date(comm.next_payment_date).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" }) : "—"}</strong>.
              Las comisiones se pagan semanalmente tras confirmación de pago del cliente.
            </p>
          </div>

          {/* Leads */}
          <section className="mt-5">
            <SectionTitle icon={<Users className="w-4 h-4" />} title="Mis clientes" count={leads.length} />
            {leads.length === 0 && (
              <div className="mt-2 rounded-2xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500"
                data-testid="empty-leads">
                Aún no tienes clientes. Comparte tu código <strong className="text-slate-800">{me.vendor_code}</strong>.
              </div>
            )}
            <div className="mt-2 space-y-2">
              {leads.map((l) => {
                const st = STATUS_LABEL[l.status] || STATUS_LABEL.pendiente;
                const paidState = l.paid_second ? "Completo" : l.paid_first ? "50%" : "Pendiente";
                return (
                  <div key={l.id} className="rounded-2xl border border-slate-200 p-3.5"
                    data-testid={`vendor-lead-${l.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{l.contact_name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {l.business_type} · {(l.selected_plan || "—").toUpperCase()}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: st.color }} />
                        <span style={{ color: st.color }}>{st.label}</span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-[11px]">
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${
                        paidState === "Completo" ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                        : paidState === "50%" ? "bg-amber-50 text-amber-700 border border-amber-100"
                        : "bg-slate-50 text-slate-600 border border-slate-200"
                      }`}>
                        Pago: {paidState}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Bizum */}
          <section className="mt-5">
            <SectionTitle icon={<Phone className="w-4 h-4" />} title="Cobro por Bizum" />
            <div className="mt-2 rounded-2xl border border-slate-200 p-3.5">
              <Label className="text-xs text-slate-600">Teléfono Bizum</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={bizum} onChange={(e) => setBizum(e.target.value)}
                  placeholder="+34 600 000 000" className="h-11 rounded-xl"
                  data-testid="input-bizum" />
                <Button onClick={saveBizum} className="btn-primary h-11 rounded-xl px-5"
                  data-testid="btn-save-bizum">Guardar</Button>
              </div>
            </div>
          </section>

          {/* Resources */}
          <section className="mt-5">
            <SectionTitle icon={<BookOpen className="w-4 h-4" />} title="Recursos" count={resources.length} />
            {resources.length === 0 ? (
              <div className="mt-2 rounded-2xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                Aún no hay recursos. El admin publicará materiales pronto.
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {resources.map((r) => (
                  <a key={r.id} href={r.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3.5 hover:bg-slate-50 active:scale-[0.99] transition"
                    data-testid={`resource-${r.id}`}>
                    <div className="h-10 w-10 rounded-xl bg-[var(--el-red)]/10 text-[var(--el-red)] flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{r.title}</div>
                      {r.description && <div className="text-xs text-slate-500 truncate">{r.description}</div>}
                    </div>
                    <Download className="w-4 h-4 text-slate-500" />
                  </a>
                ))}
              </div>
            )}
          </section>

          {/* Support */}
          <section className="mt-5 mb-6">
            <SectionTitle icon={<CheckCircle2 className="w-4 h-4" />} title="Soporte" />
            <div className="mt-2 grid grid-cols-1 gap-2">
              <a href={waUrl} target="_blank" rel="noreferrer"
                className="h-11 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2"
                data-testid="vendor-support-whatsapp">
                <MessageCircle className="w-4 h-4" />WhatsApp de soporte
              </a>
              <a href={`mailto:${config.email}`}
                className="h-11 rounded-xl border border-slate-200 hover:bg-slate-50 font-semibold flex items-center justify-center gap-2 text-sm text-slate-700"
                data-testid="vendor-support-email">
                <Mail className="w-4 h-4" />Email soporte
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

const AffiliateLink = ({ vendorCode }) => {
  const url = `${window.location.origin}/onboarding?ref=${vendorCode}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "En Línea Madrid",
          text: "Crea tu presencia digital con En Línea Madrid",
          url,
        });
      } catch (err) {
        if (err?.name !== "AbortError") toast.error("No se pudo compartir");
      }
    } else {
      copy();
    }
  };
  return (
    <div className="mt-3 space-y-2">
      <div
        className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] font-mono text-slate-700 break-all"
        data-testid="affiliate-link-url"
      >
        {url}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={copy}
          className="h-10 rounded-xl bg-slate-900 hover:bg-slate-800 active:scale-[0.99] transition text-white text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="btn-copy-affiliate-link"
        >
          <Copy className="w-3.5 h-3.5" />Copiar link
        </button>
        <button
          onClick={share}
          className="h-10 rounded-xl border border-slate-200 hover:bg-slate-50 active:scale-[0.99] transition text-slate-800 text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="btn-share-affiliate-link"
        >
          <Share2 className="w-3.5 h-3.5" />Compartir
        </button>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, icon, accent }) => (
  <div className={`rounded-2xl border p-3 ${accent ? "border-[var(--el-red)]/30 bg-[var(--el-red)]/5" : "border-slate-200 bg-white"}`}>
    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
      {icon}{label}
    </div>
    <div className={`mt-1 font-[Outfit] font-extrabold ${accent ? "text-[var(--el-red)]" : "text-slate-900"} text-xl`}>
      {value}
    </div>
  </div>
);

const SectionTitle = ({ icon, title, count }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <span className="text-slate-700">{icon}</span>
      <h3 className="font-[Outfit] font-semibold text-slate-900">{title}</h3>
    </div>
    {count !== undefined && (
      <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
        {count}
      </span>
    )}
  </div>
);
